/* What a source's page says about each of its datasets: is it profiled, is its PII reviewed, and
 * what is happening to it now.
 *
 * Pure, so the readings the table, the filters and the bulk dialog share are tested once and cannot
 * drift between them — a filter that counted "not profiled" differently from the column beside it
 * would show a chip reading 12 above a table of 11.
 *
 * **The queue's answer outranks the facts while work is outstanding.** A dataset profiled last month
 * and queued again is being profiled now, and saying "Profiled · 31 d ago" beside a running scan
 * would hide the scan. Once the job finishes, the facts — re-read — say when.
 */

import type {
  CatalogRow,
  DatasetFacts,
  ProfileJobStatus,
} from '@/shared/api/generated/catalog';

export type ProfileStatus =
  | { readonly kind: 'running' }
  | { readonly kind: 'queued' }
  /** Pending after a failed attempt: the queue is healing it, not giving up. */
  | { readonly kind: 'retrying' }
  | { readonly kind: 'failed'; readonly detail: string | null }
  | { readonly kind: 'profiled'; readonly at: string }
  | { readonly kind: 'never' }
  /** The facts could not be read, so neither "profiled" nor "never" is true. */
  | { readonly kind: 'unknown' };

export function profileStatusOf(
  facts: DatasetFacts | undefined,
  factsRead: boolean,
  job: ProfileJobStatus | undefined,
): ProfileStatus {
  if (job !== undefined) {
    if (job.state === 'running') return { kind: 'running' };
    if (job.state === 'pending')
      return job.retrying ? { kind: 'retrying' } : { kind: 'queued' };
    /* A failure is news only if it is newer than the last profile that landed: a table profiled
       yesterday whose re-profile failed last week is profiled, not failed. */
    if (
      job.state === 'failed' &&
      (facts?.last_profiled_at == null ||
        (job.finished_at !== null && job.finished_at > facts.last_profiled_at))
    ) {
      return { kind: 'failed', detail: job.detail };
    }
  }
  if (facts?.last_profiled_at != null)
    return { kind: 'profiled', at: facts.last_profiled_at };
  return factsRead ? { kind: 'never' } : { kind: 'unknown' };
}

/** Whether a dataset has a stored profile at all — what "Not profiled" filters on. `null` when the
 * facts could not be read, which is neither. */
export function hasProfile(
  facts: DatasetFacts | undefined,
  factsRead: boolean,
): boolean | null {
  if (facts?.last_profiled_at != null) return true;
  return factsRead ? false : null;
}

/** A person has approved its PII classification. A proposal is the platform's suggestion waiting
 * for a person, so it is not reviewed — the same rule the source's "PII reviewed" figure counts. */
export function piiReviewed(row: CatalogRow): boolean {
  return row.classification === 'approved';
}

export const DATASET_FILTERS = ['all', 'unprofiled', 'unreviewed'] as const;
export type DatasetFilter = (typeof DATASET_FILTERS)[number];

export function matchesFilter(
  filter: DatasetFilter,
  row: CatalogRow,
  profiled: boolean | null,
): boolean {
  switch (filter) {
    case 'all':
      return true;
    case 'unprofiled':
      /* Unread facts match nothing: "not profiled" is a claim the screen has no grounds for. */
      return profiled === false;
    case 'unreviewed':
      return !piiReviewed(row);
  }
}

export function qualifiedName(row: CatalogRow): string {
  return `${row.schema_name}.${row.object_name}`;
}

export function nameMatches(row: CatalogRow, query: string): boolean {
  const needle = query.trim().toLowerCase();
  return needle === '' || qualifiedName(row).toLowerCase().includes(needle);
}

/** A profile run's item, read from the queue. Done is the job finishing, not the request landing:
 * a queued profile has not looked at anything yet. */
export type ProfileRunState = 'queued' | 'running' | 'retrying' | 'done' | 'failed';

export function profileRunStateOf(job: ProfileJobStatus | undefined): ProfileRunState {
  if (job === undefined) return 'queued';
  switch (job.state) {
    case 'pending':
      return job.retrying ? 'retrying' : 'queued';
    case 'running':
      return 'running';
    case 'succeeded':
      return 'done';
    case 'failed':
      return 'failed';
  }
}

/** What a job looked like, as one comparable string: state and the two instants that move.
 *
 * **A run tells its own jobs apart by change, not by clock.** Comparing a job's `queued_at` with
 * the browser's start time reads a server instant against a client one, so a server a minute behind
 * leaves every finished job looking older than the run and the run waiting for ever. A snapshot of
 * the queue taken when the run starts needs no clock: a job whose signature moved since then is this
 * run's work. */
export function jobSignature(job: ProfileJobStatus | undefined): string {
  if (job === undefined) return '';
  return `${job.state}|${job.queued_at ?? ''}|${job.finished_at ?? ''}`;
}

/** A profile run's item against the snapshot taken when it started. A job pending or running now is
 * this run's work whatever the snapshot says — the request either queued it or joined it. */
export function profileRunItemOf(
  job: ProfileJobStatus | undefined,
  baseline: string | undefined,
): ProfileRunState {
  if (job === undefined) return 'queued';
  if (job.state === 'pending' || job.state === 'running') return profileRunStateOf(job);
  return jobSignature(job) === (baseline ?? '') ? 'queued' : profileRunStateOf(job);
}

/** Whether a table can be profiled at all. A vanished table is no longer at the source, so the
 * queue never takes it — and a run that listed it would wait for a job that is never coming. */
export function profilable(row: CatalogRow): boolean {
  return row.state === 'active';
}

/** A classification run's item. `review` and `clean` are both finished: one found PII waiting for
 * a person, the other found none — and "none found" is still a proposal until a person approves. */
export type ClassifyRunState = 'queued' | 'running' | 'review' | 'clean' | 'failed';

export function isFinished(state: ProfileRunState | ClassifyRunState): boolean {
  return (
    state === 'done' || state === 'failed' || state === 'review' || state === 'clean'
  );
}

/** The schemas a source's datasets sit in, largest first — from the catalogue, so reading them
 * contacts nobody's database. */
export function schemasOf(
  rows: readonly CatalogRow[],
): readonly { readonly name: string; readonly datasets: number }[] {
  const counts = new Map<string, number>();
  for (const row of rows)
    counts.set(row.schema_name, (counts.get(row.schema_name) ?? 0) + 1);
  return [...counts.entries()]
    .map(([name, datasets]) => ({ name, datasets }))
    .sort((a, b) => b.datasets - a.datasets || a.name.localeCompare(b.name));
}

/** A percentage of a whole for a figure tile, floored so 99.6% never reads as all of them. `null`
 * when there is no whole to be a percentage of. */
export function shareOf(part: number, whole: number): number | null {
  if (whole <= 0) return null;
  return Math.floor((part / whole) * 100);
}
